import React, { useState, useEffect, useRef } from "react";
import { Card, Form, Button, Tabs, Tab, Row, Col } from "react-bootstrap";
import styled from "styled-components";
import { Trash2, Plus } from "lucide-react";
import {
  NodeConfig,
  AssetClassConfig,
  SymbolConfig,
  AlgorithmConfig,
  RiskConfig,
  ScheduleConfig,
  AssetClass,
} from "../../types/algoStudio";

const PanelWrapper = styled(Card)`
  height: 100%;
  border-radius: 8px;
  box-shadow: none;
  background: #11131a;
  border: 1px solid #252a38;
  color: #e8eaed;
`;

const PanelHeader = styled(Card.Header)`
  background-color: #181b24;
  border-bottom: 1px solid #252a38;
  padding: 12px 16px;
  font-weight: 600;
  font-size: 0.8125rem;
  color: #e8eaed;
`;

const PanelBody = styled(Card.Body)`
  padding: 16px;
  overflow-y: auto;
`;

const TabContent = styled.div`
  padding: 16px 0;
`;

const IndicatorCard = styled(Card)`
  margin-bottom: 12px;
  border: 1px solid #252a38;
  background: #181b24;
`;

const ConditionCard = styled(Card)`
  margin-bottom: 12px;
  border: 1px solid #252a38;
  background: #181b24;

  .form-group {
    margin-bottom: 0;
  }

  .form-label {
    font-size: 0.8rem;
    margin-bottom: 0.25rem;
    color: #666;
  }
`;

const AddButton = styled(Button)`
  margin: 12px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const SaveButton = styled(Button)`
  margin-top: 16px;
  width: 100%;
`;

const StyledTabs = styled(Tabs)`
  .nav-link {
    color: #8b919a;
    font-weight: 500;
    font-size: 0.8125rem;
    padding: 8px 16px;
    border: none;
    border-bottom: 1px solid transparent;

    &.active {
      color: #e8eaed;
      font-weight: 600;
      background-color: #181b24;
      border-bottom: 1px solid #6b9fff;
    }
  }
`;

interface ConfigurationPanelProps {
  node: NodeConfig;
  onConfigChange: (nodeId: string, config: Record<string, any>) => void;
  onClose: () => void;
  activeAssetClass?: AssetClass | null;
}

// Get default config values for component types
const getDefaultConfigValues = (componentType: string): Record<string, any> => {
  switch (componentType) {
    case "Symbol":
      return {
        symbol: "",
        assetClass: "Stocks",
        maxCapital: 10000,
        takeProfitPercentageOverride: 0,
        stopLossPercentageOverride: 0,
        maxRiskMode: "percentage",
        maxRiskValue: 0,
        lotSize: 1,
        limitOrderUpdateValue: 50,
        // P&L fields
        openingCommission: 0.0,
        closingCommission: 0.0,
        pointSize: 0.00001,
        pointValue: 1.0,
        swapFees: 0.0,
        contractMultiplier: 100,
        quantityContracts: 1,
        pnlMultiplier: 50.0,
        tickValue: 0.25,
        contractType: "LINEAR",
        positionSize: 1.0,
        fundingRate: 0.0,
        fundingTimestamp: "",
        currentMarkPrice: 0.0
      };
    case "Trade Execution and Management":
      return {
        lotMode: "Fixed",
        lotValue: 0.1,
        maxOrders: 10,
        allowPyramiding: false,
        allowHedging: true,
        closeOnBiasFlip: false,
        stackEntriesEveryBar: true,
        enterWhenFlat: false,
        maxPyramidPositions: 3,
        pyramidSize: 0.5,
        takeProfitPercentage: 1.0,
        stopLossPercentage: 1.0,
        breakevenAfterProfit: 0,
        closeTradeAfterDrawdown: 0,
        maxPositionDuration: 0,
        addSpreadToTP: false,
        addSpreadToSL: false,
        trailingStopEnabled: false,
        trailingStopType: "Percentage",
        trailingStopAmount: 0.5,
        trailingTakeProfitEnabled: false,
        trailingTakeProfitType: "Percentage",
        trailingTakeProfitAmount: 0.5,
        orderType: "market",
        limitPriceOffset: 0.1,
        limitOrderCancelMinutes: 5,
        allowReversal: false,
        limitOrderUpdateInterval: 30,
        limitOrderUpdateValue: 100,
        useIntrabarCalculation: false,
        maxDrawdown: 1000,
        maxDrawdownPercentage: 10.0
      };
    case "Forex":
      return {
        allowedTradeDirections: "Long & Short",
        lotSize: 0.1,
        leverage: 100,
        addSpreadToStopLoss: false,
        addSpreadToTakeProfit: false,
        riskSource: "Account Balance",
        fixedCapital: 1000,
        riskPercentage: 1.0,
        maxDollarRisk: 100,
        lotMode: "Fixed"
      };
    case "Crypto Spot":
      return {
        allowedTradeDirections: "Long & Short",
        quantityPerTrade: 0.01,
        maxSpendingPerTrade: 100,
        maxTotalSpendingPerDay: 1000
      };
    case "Crypto Futures":
      return {
        allowedTradeDirections: "Long & Short",
        quantityPerTrade: 0.01,
        maxSpendingPerTrade: 100,
        maxTotalSpendingPerDay: 1000,
        contractType: "Perpetual"
      };
    default:
      return {};
  }
};

// Helper to get allowed fields for a component type
const getAllowedFieldsForType = (componentType: string): string[] => {
  switch (componentType) {
    case "Symbol":
      // Only these fields should be shown in the main form for Symbol
      // Include both old and new field names for backward compatibility
      return [
        "symbol",
        "assetClass",
        "maxCapital",
        "takeProfitPercentageOverride",
        "stopLossPercentageOverride",
        "maxRiskMode",
        "maxRiskValue",
        // Legacy fields
        "takeProfitValue",
        "stopLossValue",
        // P&L fields
        "openingCommission",
        "closingCommission",
        "pointSize",
        "pointValue",
        "swapFees",
        "contractMultiplier",
        "quantityContracts",
        "pnlMultiplier",
        "tickValue",
        "contractType",
        "positionSize",
        "fundingRate",
        "fundingTimestamp",
        "currentMarkPrice",
        "pipSize",
        "pipValueInUsd"
      ];
    case "Stocks":
      return [
        "sharesPerTrade",
        "allowedTradeDirections"
      ];
    case "Stock Options":
      return [
        "optionType",
        "optionMoneyness",
        "daysToExpiration",
        "strikeSelectionMethod",
        "maxPricePerContract",
        "contractsPerTrade",
        "allowedTradeDirections"
      ];
    case "Futures":
      return [
        "allowedTradeDirections",
        "contractsPerTrade",
        "contractSelection",
        "enableAutomaticRollover",
        "daysBeforeExpiryForRollover",
        "rolloverTime"
      ];
    case "Futures Options":
      return [
        "optionType",
        "optionMoneyness",
        "daysToExpiration",
        "strikeSelectionMethod",
        "maxPricePerContract",
        "contractsPerTrade",
        "daysBeforeExpiryForRollover",
        "rolloverTime"
      ];
    case "Forex":
      return [
        "allowedTradeDirections",
        "lotSize",
        "lotMode",
        "leverage",
        "addSpreadToStopLoss",
        "addSpreadToTakeProfit",
        "riskSource",
        "fixedCapital",
        "riskPercentage",
        "maxDollarRisk"
      ];
    case "Crypto Spot":
      return [
        "allowedTradeDirections",
        "quantityPerTrade",
        "maxSpendingPerTrade",
        "maxTotalSpendingPerDay"
      ];
    case "Crypto Futures":
      return [
        "allowedTradeDirections",
        "quantityPerTrade",
        "maxSpendingPerTrade",
        "maxTotalSpendingPerDay",
        "contractType"
      ];
    case "Schedule":
      return [
        "activeShutdown",
        "closeDay",
        "closeTime",
        "restartDay",
        "restartTime",
        "dailyStartTime",
        "dailyStopTime",
        "weeklyShutdown",
        "marketOpenTime",
        "marketCloseTime"
      ];
    case "Algorithm":
      return [
        "name", "description", "timeframe", "movingAverages",
        "entryConditions", "exitConditions", "indicators"
      ];
    case "Trade Execution and Management":
      return [
        "maxOrders", "allowPyramiding", "stackEntriesEveryBar", "allowHedging", "closeOnBiasFlip", "maxPyramidPositions",
        "pyramidSize", "takeProfitPercentage", "stopLossPercentage",
        "breakevenAfterProfit", "closeTradeAfterDrawdown", "addSpreadToTP",
        "addSpreadToSL", "trailingStopEnabled", "trailingStopType",
        "trailingStopAmount", "trailingTakeProfitEnabled", "trailingTakeProfitType",
        "trailingTakeProfitAmount", "orderType", "limitPriceOffset",
        "limitOrderCancelMinutes", "allowReversal",
        "limitOrderUpdateInterval", "limitOrderUpdateValue", "useIntrabarCalculation",
        "maxDrawdown", "maxDrawdownPercentage"
      ];
    default:
      // For unknown types, show all non-complex fields (backward compatibility)
      return [];
  }
};

// Cleanup function to remove invalid fields from component configs
const cleanupConfig = (config: Record<string, any>, componentType: string): Record<string, any> => {

  const allowedFields = getAllowedFieldsForType(componentType);
  const defaultValues = getDefaultConfigValues(componentType);

  // If there's a whitelist, clean and initialize missing fields
  if (allowedFields.length > 0) {
    const cleaned: Record<string, any> = {};
    allowedFields.forEach(field => {
      // Use existing value if present, otherwise use default if available
      if (config.hasOwnProperty(field)) {
        cleaned[field] = config[field];
      } else if (defaultValues.hasOwnProperty(field)) {
        // Initialize missing fields with defaults
        cleaned[field] = defaultValues[field];
      }
    });

    // Special handling for legacy field synchronization in Symbol component
    if (componentType === "Symbol") {
      // Map of new names to legacy names
      const fieldMapping: Record<string, string> = {
        "takeProfitPercentageOverride": "takeProfitValue",
        "stopLossPercentageOverride": "stopLossValue",
        "pointSize": "pipSize",
        "pointValue": "pipValueInUsd"
      };

      Object.entries(fieldMapping).forEach(([newName, oldName]) => {
        // If the current field being cleaned has a value in the input config, use it for both
        if (config.hasOwnProperty(newName)) {
          cleaned[newName] = config[newName];
          cleaned[oldName] = config[newName];
        } else if (config.hasOwnProperty(oldName)) {
          // If only legacy exists (e.g. loading from backend), sync to new name
          cleaned[newName] = config[oldName];
          cleaned[oldName] = config[oldName];
        }
      });
    }

    if (componentType === "Trade Execution and Management") {
      if (cleaned.stackEntriesEveryBar === undefined) {
        cleaned.stackEntriesEveryBar =
          cleaned.enterWhenFlat === undefined ? true : !cleaned.enterWhenFlat;
      }
      if (cleaned.enterWhenFlat === undefined) {
        cleaned.enterWhenFlat = !cleaned.stackEntriesEveryBar;
      }
    }

    return cleaned;
  }

  // No whitelist - return config as-is (for asset classes)
  return config;
};

const ConfigurationPanel: React.FC<ConfigurationPanelProps> = ({
  node,
  onConfigChange,
  onClose,
  activeAssetClass,
}) => {
  const [activeTab, setActiveTab] = useState("indicators");
  const [localConfig, setLocalConfig] = useState(() => cleanupConfig(node.config, node.type));
  const lastNodeIdRef = useRef({ id: node.id, type: node.type });
  const resolvedAssetClass: AssetClass =
    (activeAssetClass ||
      (node.type === "Symbol" ? (localConfig.assetClass as AssetClass) : null) ||
      "Stocks") as AssetClass;

  useEffect(() => {
    // Check if node ID or type changed
    const nodeChanged = node.id !== lastNodeIdRef.current.id;
    const typeChanged = node.type !== lastNodeIdRef.current.type;

    if (nodeChanged || typeChanged) {
      // Clean the config when switching nodes or types to remove invalid fields
      // This is critical to prevent fields from other components appearing
      const cleaned = cleanupConfig(node.config, node.type);
      setLocalConfig(cleaned);
      // Always update parent with cleaned config to ensure consistency
      // This prevents fields from other components persisting when switching
      onConfigChange(node.id, cleaned);
      lastNodeIdRef.current = { id: node.id, type: node.type };
    } else {
      // Same node and type, but might have been polluted - do a deep check
      const allowedFields = getAllowedFieldsForType(node.type);
      if (allowedFields.length > 0) {
        const hasInvalidFields = Object.keys(node.config).some(key => {
          return !allowedFields.includes(key);
        });

        // If config was polluted with invalid fields, clean it aggressively
        if (hasInvalidFields) {
          const cleaned = cleanupConfig(node.config, node.type);
          setLocalConfig(cleaned);
          onConfigChange(node.id, cleaned);
        }
      }
    }
    // Note: We don't clean on config changes for the same node to avoid interfering with user input
    // The cleanupConfig in handleChange will ensure invalid fields are removed when user makes changes
  }, [node.id, node.type, node.config]);

  // Sync symbol asset class with algorithm-wide asset class
  useEffect(() => {
    if (node.type === "Symbol" && activeAssetClass && localConfig.assetClass !== activeAssetClass) {
      console.log(`Syncing symbol asset class to ${activeAssetClass}`);
      const updatedConfig = { ...localConfig, assetClass: activeAssetClass };
      setLocalConfig(updatedConfig);
      onConfigChange(node.id, updatedConfig);
    }
  }, [node.type, node.id, activeAssetClass, localConfig.assetClass, onConfigChange]);

  // Initialize P&L fields for Symbol nodes
  useEffect(() => {
    if (node.type !== "Symbol") return;

    const getPnlFieldsForAssetClass = (assetClass: string): string[] => {
      const baseFields = ["openingCommission", "closingCommission"];
      switch (assetClass) {
        case "Forex":
          return [...baseFields, "pointSize", "pointValue", "swapFees"];
        case "Stocks":
        case "Crypto Spot":
          return baseFields;
        case "Stock Options":
        case "Futures Options":
          return [...baseFields, "contractMultiplier", "quantityContracts"];
        case "Futures":
          return [...baseFields, "pnlMultiplier", "tickValue"];
        case "Crypto Futures":
          return [...baseFields, "contractType", "positionSize", "fundingRate", "fundingTimestamp", "currentMarkPrice"];
        default:
          return [
            ...baseFields,
            "pointSize", "pointValue", "swapFees",
            "contractMultiplier", "quantityContracts",
            "pnlMultiplier", "tickValue",
            "contractType", "positionSize", "fundingRate", "fundingTimestamp", "currentMarkPrice"
          ];
      }
    };

    const pnlFields = getPnlFieldsForAssetClass(resolvedAssetClass || "");
    const defaultPnlValues: Record<string, any> = {
      openingCommission: 0.0,
      closingCommission: 0.0,
      pointSize: 0.00001,
      pointValue: 1.0,
      swapFees: 0.0,
      contractMultiplier: 100,
      quantityContracts: 1,
      pnlMultiplier: 50.0,
      tickValue: 0.25,
      contractType: "LINEAR",
      positionSize: 1.0,
      fundingRate: 0.0,
      fundingTimestamp: "",
      currentMarkPrice: 0.0
    };

    let updatedConfig = { ...localConfig };
    let configUpdated = false;

    pnlFields.forEach(field => {
      if (!(field in updatedConfig)) {
        updatedConfig[field] = defaultPnlValues[field];
        configUpdated = true;
      }
    });

    if (configUpdated) {
      // Use onConfigChange to update parent, passing the new config
      // We don't call setLocalConfig here because onConfigChange will trigger a prop update
      // which might trigger the main useEffect to update localConfig if we're not careful.
      // However, handleConfigChange in AlgoStudio updates selectedNode synchronousy.
      // But to be safe and avoid loops, we check if changes are actually needed.
      onConfigChange(node.id, updatedConfig);
      // We also update localConfig to reflect changes immediately in UI
      setLocalConfig(updatedConfig);
    }
  }, [node.type, node.id, resolvedAssetClass, onConfigChange]); // Exclude localConfig to prevent loops

  // Helper to determine if a field should be treated as numeric
  const isNumericField = (key: string): boolean => {
    const numericFields = [
      "lotSize", "lotValue", "maxCapital", "maxRiskValue", "limitOrderUpdateValue",
      "limitOrderUpdateInterval", "takeProfitPercentage", "stopLossPercentage",
      "takeProfitPercentageOverride", "stopLossPercentageOverride",
      "takeProfitValue", "stopLossValue", "maxOrders", "maxPyramidPositions",
      "pyramidSize", "breakevenAfterProfit", "closeTradeAfterDrawdown",
      "maxPositionDuration", "trailingStopAmount", "trailingTakeProfitAmount",
      "limitPriceOffset", "maxDrawdown", "maxDrawdownPercentage",
      // P&L Fields
      "openingCommission", "closingCommission", "pointSize", "pointValue",
      "swapFees", "contractMultiplier", "quantityContracts", "pnlMultiplier", "tickValue", "positionSize", "sharesPerTrade", "maxPricePerContract",
      "contractsPerTrade", "daysToExpiration", "daysBeforeExpiryForRollover",
      "quantityPerTrade", "maxSpendingPerTrade", "maxTotalSpendingPerDay",
      "fixedCapital", "riskPercentage", "maxDollarRisk", "leverage",
      "currentMarkPrice", "fundingRate", "pipSize", "pipValueInUsd"
    ];
    return numericFields.includes(key);
  };

  const handleChange = (key: string, value: any) => {
    // Map new field names back to old ones for backward compatibility
    const fieldNameReverseMap: Record<string, string> = {
      "takeProfitPercentageOverride": "takeProfitValue",
      "stopLossPercentageOverride": "stopLossValue"
    };

    // Use the old field name for storage if mapping exists
    const storageKey = fieldNameReverseMap[key] || key;

    // Update the config with the new value
    const newConfig = {
      ...localConfig,
      [key]: value,
      [storageKey]: value,
    };

    // If key has a special mapping in Symbols, sync it
    if (node.type === "Symbol") {
      const fieldNameMap: Record<string, string> = {
        "takeProfitPercentageOverride": "takeProfitValue",
        "stopLossPercentageOverride": "stopLossValue",
        "pointSize": "pipSize",
        "pointValue": "pipValueInUsd"
      };

      // Sync in both directions
      if (fieldNameMap[key]) {
        newConfig[fieldNameMap[key]] = value;
      } else {
        // Check if it's a legacy key being updated (though usually we display new ones)
        const reverseKey = Object.keys(fieldNameMap).find(k => fieldNameMap[k] === key);
        if (reverseKey) {
          newConfig[reverseKey] = value;
        }
      }
    }

    // Force assetClass to match algorithm-wide asset class for Symbols
    if (node.type === "Symbol" && activeAssetClass) {
      newConfig.assetClass = activeAssetClass;
    }

    if (node.type === "Trade Execution and Management") {
      if (key === "stackEntriesEveryBar") {
        newConfig.enterWhenFlat = !value;
        if (value) {
          newConfig.closeOnBiasFlip = true;
        }
      }
    }

    // Clean up the config to remove invalid fields
    const cleanedConfig = cleanupConfig(newConfig, node.type);

    setLocalConfig(cleanedConfig);
    onConfigChange(node.id, cleanedConfig);
  };

  const renderConfigField = (key: string, value: any) => {
    const fieldLabels: Record<string, string> = {
      stackEntriesEveryBar:
        "Stack entry every signal bar while trend holds (independent mode)",
      allowHedging: "Allow hedging (long and short at the same time)",
      closeOnBiasFlip: "Close all positions in old direction when bias flips",
    };

    // Convert key to display format (e.g., "maxCapital" -> "Max Capital")
    let displayKey = fieldLabels[key] || key
      .replace(/([A-Z])/g, " $1")
      .replace(/^./, (str) => str.toUpperCase());

    // Nomenclature overrides: Pip -> Point
    if (key === "pipSize") displayKey = "Point Size";
    if (key === "pipValueInUsd") displayKey = "Point Value";

    // Handle different value types and predefined options
    if (typeof value === "boolean") {
      return (
        <Form.Group key={key} className="mb-3">
          <Form.Check
            type="checkbox"
            id={key}
            label={displayKey}
            checked={value}
            onChange={(e) => handleChange(key, e.target.checked)}
          />
          {key === "stackEntriesEveryBar" && (
            <Form.Text className="text-muted">
              Opens a new position on each signal bar while entry conditions hold.
              Use with &quot;Close on bias flip&quot; to exit all legs when the trend reverses.
            </Form.Text>
          )}
        </Form.Group>
      );
    }

    // Handle predefined options
    const getOptions = (field: string) => {
      switch (field) {
        // Algorithm Settings
        case "timeframe":
          return [
            { value: "1m", label: "1 Minute" },
            { value: "5m", label: "5 Minutes" },
            { value: "15m", label: "15 Minutes" },
            { value: "30m", label: "30 Minutes" },
            { value: "45m", label: "45 Minutes" },
            { value: "1h", label: "1 Hour" },
            { value: "4h", label: "4 Hours" },
            { value: "1d", label: "1 Day" },
          ];

        // Symbol Settings
        case "assetClass":
          return [
            { value: "Stocks", label: "Stocks" },
            { value: "Stock Options", label: "Stock Options" },
            { value: "Futures", label: "Futures" },
            { value: "Futures Options", label: "Futures Options" },
            { value: "Forex", label: "Forex" },
            { value: "Crypto Spot", label: "Crypto Spot" },
            { value: "Crypto Futures", label: "Crypto Futures" },
          ];

        // Risk Management Settings
        case "riskSource":
          return [
            { value: "Account Balance", label: "Account Balance" },
            { value: "Fixed Amount", label: "Fixed Amount" },
            { value: "Account Equity", label: "Account Equity" },
          ];
        case "lotMode":
          return [
            { value: "Fixed", label: "Fixed" },
            { value: "Risk", label: "Risk" },
            { value: "Percentage", label: "Percentage" },
          ];
        case "trailingStopType":
        case "trailingTakeProfitType":
        case "trailingSLType":
        case "trailingTPType":
          return [
            { value: "Percentage", label: "Percentage" },
            { value: "Fixed Amount", label: "Fixed Amount" },
          ];
        case "orderType":
          return [
            { value: "market", label: "Market" },
            { value: "limit", label: "Limit" },
          ];

        // Asset Class Settings
        case "allowedTradeDirections":
          return [
            { value: "Long Only", label: "Long Only" },
            { value: "Short Only", label: "Short Only" },
            { value: "Both", label: "Both" },
          ];
        case "optionType":
          return [
            { value: "Call", label: "Call" },
            { value: "Put", label: "Put" },
            { value: "Both", label: "Both" },
          ];
        case "optionMoneyness":
          return [
            { value: "ITM", label: "In-the-Money (ITM)" },
            { value: "ATM", label: "At-the-Money (ATM)" },
            { value: "OTM", label: "Out-of-the-Money (OTM)" },
          ];
        case "strikeSelectionMethod":
          return [
            { value: "Closest ATM", label: "Closest ATM" },
            { value: "Delta Range", label: "Delta Range" },
            { value: "Price Offset", label: "Price Offset" },
            { value: "Percent Offset", label: "Percent Offset" },
            { value: "Specific Strikes", label: "Specific Strikes" },
          ];
        case "contractSelection":
          return [
            { value: "Front Month", label: "Front Month" },
            { value: "Next Month", label: "Next Month" },
            { value: "Highest Volume", label: "Highest Volume" },
            { value: "Specific Code", label: "Specific Code" },
          ];
        case "leverage":
          return [
            { value: "1:50", label: "1:50" },
            { value: "1:100", label: "1:100" },
            { value: "1:200", label: "1:200" },
            { value: "1:500", label: "1:500" },
            { value: "1:1000", label: "1:1000" },
          ];
        case "maxRiskMode":
          return [
            { value: "percentage", label: "Percentage (%)" },
            { value: "dollar", label: "Dollar Amount ($)" },
          ];
        case "contractType":
          // This handles both regular contractType and crypto contractType
          if (localConfig.assetClass === "Crypto Futures") {
            return [
              { value: "LINEAR", label: "Linear Contract" },
              { value: "INVERSE", label: "Inverse Contract" },
            ];
          } else {
            return [
              { value: "Perpetual Swap", label: "Perpetual Swap" },
              { value: "Dated Future", label: "Dated Future" },
            ];
          }

        // Schedule Settings
        case "closeDay":
        case "restartDay":
        case "weeklyCloseDay":
        case "weeklyRestartDay":
          return [
            { value: "Monday", label: "Monday" },
            { value: "Tuesday", label: "Tuesday" },
            { value: "Wednesday", label: "Wednesday" },
            { value: "Thursday", label: "Thursday" },
            { value: "Friday", label: "Friday" },
            { value: "Saturday", label: "Saturday" },
            { value: "Sunday", label: "Sunday" },
          ];

        default:
          return null;
      }
    };

    const options = getOptions(key);
    if (options) {
      return (
        <Form.Group key={key} className="mb-3">
          <Form.Label>{displayKey}</Form.Label>
          <Form.Select
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
          >
            {options.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
      );
    }

    // Handle numeric inputs with appropriate step values
    // Check key against strict number list OR if value is number
    if (isNumericField(key) || typeof value === "number") {
      const step =
        key === "lotSize"
          ? 1
          : key === "limitOrderUpdateValue"
            ? 1
            : key === "limitOrderUpdateInterval"
              ? 1
              : key.includes("Percentage") || key.includes("percentage")
                ? 0.1
                : key === "pointSize"
                  ? 0.00001
                  : key === "pointValue"
                    ? 0.01
                    : key === "tickValue"
                      ? 0.01
                      : key === "pnlMultiplier"
                        ? 0.1
                        : key === "positionSize"
                          ? 0.01
                          : 1;

      const min = key === "limitOrderUpdateInterval" ? 1 :
        key === "pointSize" || key === "pipSize" ? 0.00001 :
          key === "pointValue" || key === "pipValueInUsd" ? 0.01 :
            key === "tickValue" ? 0.01 :
              key === "pnlMultiplier" ? 0.1 :
                key === "positionSize" ? 0.01 :
                  0;

      // Get field description for P&L fields
      const getFieldDescription = (fieldKey: string) => {
        const descriptions: { [key: string]: string } = {
          pointSize: "Smallest unique price increment (e.g., 0.00001)",
          pointValue: "Dollar value of one point move for your lot size",
          contractMultiplier: "Number of shares one contract represents (typically 100)",
          quantityContracts: "Number of options contracts",
          pnlMultiplier: "Dollar value of one-point move in the contract",
          tickValue: "Dollar value of one minimum price move",
          positionSize: "Quantity of the asset (Linear) or contracts (Inverse)",
          pipSize: "Smallest unique price increment (e.g., 0.00001)",
          pipValueInUsd: "Dollar value of one point move for your lot size"
        };
        return descriptions[fieldKey];
      };

      const description = getFieldDescription(key);

      return (
        <Form.Group key={key} className="mb-3">
          <Form.Label>{displayKey}</Form.Label>
          {description && (
            <Form.Text className="text-muted mb-2 d-block">
              {description}
            </Form.Text>
          )}
          <Form.Control
            type="number"
            value={value === undefined || value === null ? "" : value}
            onChange={(e) => {
              const val = e.target.value;
              // Allow setting empty string or keeping decimal string (e.g. "0.")
              if (val === "" || val === ".") {
                handleChange(key, val);
              } else {
                // Try to parse, but if it ends in dot, keep as string
                if (val.endsWith(".")) {
                  handleChange(key, val);
                } else {
                  const num = parseFloat(val);
                  handleChange(key, isNaN(num) ? val : num);
                }
              }
            }}
            step={step}
            min={min}
          />
        </Form.Group>
      );
    }

    // Handle time inputs
    if (key.toLowerCase().includes("time")) {
      return (
        <Form.Group key={key} className="mb-3">
          <Form.Label>{displayKey}</Form.Label>
          <Form.Control
            type="time"
            value={value}
            onChange={(e) => handleChange(key, e.target.value)}
          />
        </Form.Group>
      );
    }

    // Handle objects and arrays - don't display as text inputs
    // Arrays are also objects in JavaScript, so check Array.isArray first
    if (Array.isArray(value)) {
      // Skip rendering arrays - they should be handled by specialized components
      return null;
    }

    if (typeof value === "object" && value !== null && !(value instanceof Date)) {
      // Skip rendering complex objects - they should be handled by specialized components
      return null;
    }

    // Default to text input for strings and other types
    const stringValue = value != null ? String(value) : "";
    return (
      <Form.Group key={key} className="mb-3">
        <Form.Label>{displayKey}</Form.Label>
        <Form.Control
          type="text"
          value={stringValue}
          onChange={(e) => handleChange(key, e.target.value)}
        />
      </Form.Group>
    );
  };



  const renderGeneralSettings = () => (
    <div className="mb-4">
      <Form.Group className="mb-3">
        <Form.Label>Algorithm Name</Form.Label>
        <Form.Control
          type="text"
          value={localConfig.name || ""}
          onChange={(e) => handleChange("name", e.target.value)}
          placeholder="Enter algorithm name"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Description</Form.Label>
        <Form.Control
          as="textarea"
          rows={3}
          value={localConfig.description || ""}
          onChange={(e) => handleChange("description", e.target.value)}
          placeholder="Enter algorithm description"
        />
      </Form.Group>

      <Form.Group className="mb-3">
        <Form.Label>Timeframe</Form.Label>
        <Form.Select
          value={localConfig.timeframe || "1h"}
          onChange={(e) => handleChange("timeframe", e.target.value)}
        >
          <option value="1m">1 Minute</option>
          <option value="5m">5 Minutes</option>
          <option value="15m">15 Minutes</option>
          <option value="1h">1 Hour</option>
          <option value="4h">4 Hours</option>
          <option value="1d">1 Day</option>
        </Form.Select>
      </Form.Group>
    </div>
  );

  const renderIndicatorsTab = () => (
    <TabContent>
      <div className="p-3">
        <h5>Moving Averages</h5>
        {localConfig.movingAverages?.map((ma: any, index: number) => (
          <Row key={index} className="mb-3 align-items-center">
            <Col>
              <Form.Select
                value={ma.type}
                onChange={(e) => {
                  const newMAs = [...localConfig.movingAverages];
                  newMAs[index] = { ...ma, type: e.target.value };
                  handleChange("movingAverages", newMAs);
                }}
              >
                <option value="SMA">Simple Moving Average (SMA)</option>
                <option value="EMA">Exponential Moving Average (EMA)</option>
                <option value="LWMA">
                  Linear Weighted Moving Average (LWMA)
                </option>
                <option value="SMMA">Smoothed Moving Average (SMMA)</option>
              </Form.Select>
            </Col>
            <Col>
              <Form.Control
                type="number"
                min="1"
                value={ma.period}
                onChange={(e) => {
                  const newMAs = [...localConfig.movingAverages];
                  newMAs[index] = {
                    ...ma,
                    period: parseInt(e.target.value),
                  };
                  handleChange("movingAverages", newMAs);
                }}
                placeholder="Period"
              />
            </Col>
            <Col>
              <Form.Select
                value={ma.priceType}
                onChange={(e) => {
                  const newMAs = [...localConfig.movingAverages];
                  newMAs[index] = { ...ma, priceType: e.target.value };
                  handleChange("movingAverages", newMAs);
                }}
              >
                <option value="close">Close</option>
                <option value="open">Open</option>
                <option value="high">High</option>
                <option value="low">Low</option>
                <option value="hl2">HL/2</option>
                <option value="hlc3">HLC/3</option>
                <option value="ohlc4">OHLC/4</option>
              </Form.Select>
            </Col>
            <Col xs="auto">
              <Button
                variant="danger"
                onClick={() => {
                  const maToDelete = localConfig.movingAverages[index];
                  const newMAs = [...localConfig.movingAverages];
                  newMAs.splice(index, 1);

                  // Filter out any conditions that reference the deleted MA
                  const filterConditions = (conditions: any[]) => {
                    return conditions?.filter(cond => {
                      const isIndicator1 = cond.indicator1.type === maToDelete.type &&
                        cond.indicator1.period === maToDelete.period;
                      const isIndicator2 = cond.indicator2.type === maToDelete.type &&
                        cond.indicator2.period === maToDelete.period;
                      return !isIndicator1 && !isIndicator2;
                    }) || [];
                  };

                  const newEntryConditions = {
                    buyConditions: filterConditions(localConfig.entryConditions?.buyConditions),
                    sellConditions: filterConditions(localConfig.entryConditions?.sellConditions)
                  };

                  const newExitConditions = {
                    buyExitConditions: filterConditions(localConfig.exitConditions?.buyExitConditions),
                    sellExitConditions: filterConditions(localConfig.exitConditions?.sellExitConditions)
                  };

                  // Update all state at once
                  const newConfig = {
                    ...localConfig,
                    movingAverages: newMAs,
                    entryConditions: newEntryConditions,
                    exitConditions: newExitConditions
                  };

                  // Use internal update logic directly instead of single handleChange calls
                  setLocalConfig(newConfig);
                  onConfigChange(node.id, newConfig);
                }}
              >
                <Trash2 size={16} />
              </Button>
            </Col>
          </Row>
        ))}
        <Button
          variant="primary"
          onClick={() => {
            const newMA = {
              type: "EMA",
              period: 21,
              priceType: "close",
              shift: 0,
            };
            handleChange("movingAverages", [
              ...(localConfig.movingAverages || []),
              newMA,
            ]);
          }}
        >
          <Plus size={16} className="me-2" />
          Add Moving Average
        </Button>
      </div>
    </TabContent>
  );

  const renderConditionRow = (
    condition: any,
    index: number,
    conditions: any[],
    setConditions: (conditions: any[]) => void
  ) => (
    <Row key={index} className="mb-3 align-items-center">
      <Col>
        <Form.Select
          value={`${condition.indicator1.type}_${condition.indicator1.period}`}
          onChange={(e) => {
            const [type, period] = e.target.value.split("_");
            const newConditions = [...conditions];
            newConditions[index] = {
              ...condition,
              indicator1: {
                type,
                period: parseInt(period),
                priceType: "close",
                shift: 0,
              },
            };
            setConditions(newConditions);
          }}
        >
          {localConfig.movingAverages?.map((ma: any) => (
            <option
              key={`${ma.type}_${ma.period}`}
              value={`${ma.type}_${ma.period}`}
            >
              {ma.type} {ma.period}
            </option>
          ))}
        </Form.Select>
      </Col>
      <Col>
        <Form.Select
          value={condition.relationship}
          onChange={(e) => {
            const newConditions = [...conditions];
            newConditions[index] = {
              ...condition,
              relationship: e.target.value,
            };
            setConditions(newConditions);
          }}
        >
          <option value="Crosses above">Crosses above</option>
          <option value="Crosses below">Crosses below</option>
          <option value="Is above">Is above</option>
          <option value="Is below">Is below</option>
        </Form.Select>
      </Col>
      <Col>
        <Form.Select
          value={`${condition.indicator2.type}_${condition.indicator2.period}`}
          onChange={(e) => {
            const [type, period] = e.target.value.split("_");
            const newConditions = [...conditions];
            newConditions[index] = {
              ...condition,
              indicator2: {
                type,
                period: parseInt(period),
                priceType: "close",
                shift: 0,
              },
            };
            setConditions(newConditions);
          }}
        >
          {localConfig.movingAverages?.map((ma: any) => (
            <option
              key={`${ma.type}_${ma.period}`}
              value={`${ma.type}_${ma.period}`}
            >
              {ma.type} {ma.period}
            </option>
          ))}
        </Form.Select>
      </Col>
      <Col xs="auto">
        <Button
          variant="danger"
          onClick={() => {
            setConditions(conditions.filter((_, i) => i !== index));
          }}
        >
          <Trash2 size={16} />
        </Button>
      </Col>
    </Row>
  );

  const renderEntryLogicTab = () => (
    <TabContent>
      <div className="p-3">
        <h5>Buy Conditions</h5>
        {localConfig.entryConditions?.buyConditions?.map(
          (condition: any, index: number) =>
            renderConditionRow(
              condition,
              index,
              localConfig.entryConditions.buyConditions,
              (newConditions) =>
                handleChange("entryConditions", {
                  ...localConfig.entryConditions,
                  buyConditions: newConditions,
                })
            )
        )}
        <Button
          variant="primary"
          onClick={() => {
            if (localConfig.movingAverages?.length >= 2) {
              const newCondition = {
                indicator1: localConfig.movingAverages[0],
                relationship: "Crosses above",
                indicator2: localConfig.movingAverages[1],
              };
              handleChange("entryConditions", {
                ...localConfig.entryConditions,
                buyConditions: [
                  ...(localConfig.entryConditions?.buyConditions || []),
                  newCondition,
                ],
              });
            }
          }}
        >
          <Plus size={16} className="me-2" />
          Add Buy Condition
        </Button>

        <h5 className="mt-4">Sell Conditions</h5>
        {localConfig.entryConditions?.sellConditions?.map(
          (condition: any, index: number) =>
            renderConditionRow(
              condition,
              index,
              localConfig.entryConditions.sellConditions,
              (newConditions) =>
                handleChange("entryConditions", {
                  ...localConfig.entryConditions,
                  sellConditions: newConditions,
                })
            )
        )}
        <Button
          variant="primary"
          onClick={() => {
            if (localConfig.movingAverages?.length >= 2) {
              const newCondition = {
                indicator1: localConfig.movingAverages[0],
                relationship: "Crosses below",
                indicator2: localConfig.movingAverages[1],
              };
              handleChange("entryConditions", {
                ...localConfig.entryConditions,
                sellConditions: [
                  ...(localConfig.entryConditions?.sellConditions || []),
                  newCondition,
                ],
              });
            }
          }}
        >
          <Plus size={16} className="me-2" />
          Add Sell Condition
        </Button>
      </div>
    </TabContent>
  );

  const renderRiskManagementSections = () => {
    const hideForNonForex = new Set([
      "addSpreadToTP",
      "addSpreadToSL",
    ]);

    const shouldShowRiskField = (field: string) => {
      if (resolvedAssetClass !== "Forex" && hideForNonForex.has(field)) {
        return false;
      }
      return localConfig[field] !== undefined;
    };

    // Define field groups for Trade Execution and Management
    const riskSections = {
      "Risk Management": [
        "maxOrders"
      ],
      "Position Management": [
        "stackEntriesEveryBar",
        "allowPyramiding",
        "allowHedging",
        "closeOnBiasFlip",
        "maxPyramidPositions",
        "pyramidSize",
        "maxPositionDuration"
      ],
      "Take Profit & Stop Loss": [
        "takeProfitPercentage",
        "stopLossPercentage",
        "breakevenAfterProfit",
        "closeTradeAfterDrawdown",
        "addSpreadToTP",
        "addSpreadToSL"
      ],
      "Trailing Settings": [
        "trailingStopEnabled",
        "trailingStopType",
        "trailingStopAmount",
        "trailingTakeProfitEnabled",
        "trailingTakeProfitType",
        "trailingTakeProfitAmount"
      ],
      "Order Execution": [
        "orderType",
        "limitPriceOffset",
        "stopPriceOffset",
        "limitOrderCancelMinutes",
        "allowReversal",
        "limitOrderUpdateInterval",
        "limitOrderUpdateValue",
        "useIntrabarCalculation"
      ],
      "Drawdown Protection": [
        "maxDrawdown",
        "maxDrawdownPercentage"
      ]
    };

    return (
      <>
        {Object.entries(riskSections).map(([sectionName, fields]) => {
          // Check if any fields in this section have values
          const hasFields = fields.some(field => localConfig[field] !== undefined);
          if (!hasFields) return null;

          return (
            <Card key={sectionName} className="mb-4">
              <Card.Header style={{
                backgroundColor: '#181b24',
                borderBottom: '1px solid #6b9fff',
                fontWeight: 600,
                fontSize: '0.875rem',
                padding: '12px 16px',
                color: '#e8eaed',
              }}>
                {sectionName}
              </Card.Header>
              <Card.Body style={{ padding: '16px' }}>
                {fields.map((field) => {
                  if (!shouldShowRiskField(field)) return null;
                  return renderConfigField(field, localConfig[field]);
                })}
              </Card.Body>
            </Card>
          );
        })}
      </>
    );
  };

  const renderExitLogicTab = () => (
    <TabContent>
      <div className="p-3">
        <h5>Buy Exit Conditions</h5>
        {localConfig.exitConditions?.buyExitConditions?.map(
          (condition: any, index: number) =>
            renderConditionRow(
              condition,
              index,
              localConfig.exitConditions.buyExitConditions,
              (newConditions) =>
                handleChange("exitConditions", {
                  ...localConfig.exitConditions,
                  buyExitConditions: newConditions,
                })
            )
        )}
        <Button
          variant="primary"
          className="mb-4"
          onClick={() => {
            if (localConfig.movingAverages?.length >= 2) {
              const newCondition = {
                indicator1: localConfig.movingAverages[0],
                relationship: "Crosses below",
                indicator2: localConfig.movingAverages[1],
              };
              handleChange("exitConditions", {
                ...localConfig.exitConditions,
                buyExitConditions: [
                  ...(localConfig.exitConditions?.buyExitConditions || []),
                  newCondition,
                ],
              });
            }
          }}
        >
          <Plus size={16} className="me-2" />
          Add Buy Exit Condition
        </Button>

        <h5>Sell Exit Conditions</h5>
        {localConfig.exitConditions?.sellExitConditions?.map(
          (condition: any, index: number) =>
            renderConditionRow(
              condition,
              index,
              localConfig.exitConditions.sellExitConditions,
              (newConditions) =>
                handleChange("exitConditions", {
                  ...localConfig.exitConditions,
                  sellExitConditions: newConditions,
                })
            )
        )}
        <Button
          variant="primary"
          onClick={() => {
            if (localConfig.movingAverages?.length >= 2) {
              const newCondition = {
                indicator1: localConfig.movingAverages[0],
                relationship: "Crosses above",
                indicator2: localConfig.movingAverages[1],
              };
              handleChange("exitConditions", {
                ...localConfig.exitConditions,
                sellExitConditions: [
                  ...(localConfig.exitConditions?.sellExitConditions || []),
                  newCondition,
                ],
              });
            }
          }}
        >
          <Plus size={16} className="me-2" />
          Add Sell Exit Condition
        </Button>
      </div>
    </TabContent>
  );

  return (
    <PanelWrapper>
      <PanelHeader>
        <div className="d-flex justify-content-between align-items-center">
          <span>{node.type}</span>
          <Button variant="link" className="p-0" onClick={onClose}>
            Close
          </Button>
        </div>
      </PanelHeader>
      <PanelBody>
        {node.type === "Algorithm" ? (
          <>
            {renderGeneralSettings()}
            <StyledTabs
              activeKey={activeTab}
              onSelect={(k) => setActiveTab(k || "indicators")}
              className="mb-3"
            >
              <Tab eventKey="indicators" title="Indicators">
                {renderIndicatorsTab()}
              </Tab>
              <Tab eventKey="entry" title="Entry Logic">
                {renderEntryLogicTab()}
              </Tab>
              <Tab eventKey="exit" title="Exit Logic">
                {renderExitLogicTab()}
              </Tab>
            </StyledTabs>
          </>
        ) : (
          <Form>
            {(() => {
              // Define allowed fields for each component type
              const allowedFields = getAllowedFieldsForType(node.type);

              // For Symbol component, use strict whitelist with asset-class filtering
              if (node.type === "Symbol" && allowedFields.length > 0) {
                // Map old field names to new ones for display
                const fieldNameMap: Record<string, string> = {
                  "takeProfitValue": "takeProfitPercentageOverride",
                  "stopLossValue": "stopLossPercentageOverride"
                };

                const symbolFields = Object.entries(localConfig)
                  .filter(([key]) => {
                    // Only render fields in the allowed list
                    // Legacy names match new ones in allowedFields, so we skip the legacy ones to avoid duplication
                    if (fieldNameMap.hasOwnProperty(key)) {
                      return false;
                    }

                    if (!allowedFields.includes(key)) {
                      return false;
                    }

                    // Remove lotSize for non-Forex assets
                    if (key === "lotSize" && resolvedAssetClass !== "Forex") {
                      return false;
                    }

                    // Exclude P&L fields from main render (they are in the P&L section)
                    const pnlFields = [
                      "openingCommission", "closingCommission",
                      "pointSize", "pointValue", "swapFees",
                      "contractMultiplier", "quantityContracts",
                      "pnlMultiplier", "tickValue",
                      "contractType", "positionSize", "fundingRate", "fundingTimestamp", "currentMarkPrice",
                      "pipSize", "pipValueInUsd"
                    ];
                    if (pnlFields.includes(key)) {
                      return false;
                    }

                    return true;
                  })
                  .map(([key, value]) => {
                    // Use mapped field name for display if mapping exists
                    const displayKey = fieldNameMap[key] || key;
                    // Always use the original value from the config
                    return renderConfigField(displayKey, value);
                  });

                return symbolFields;
              }

              // Special handling for Trade Execution and Management with sections
              if (node.type === "Trade Execution and Management") {
                return renderRiskManagementSections();
              }

              // For other component types with whitelist, use it
              if (allowedFields.length > 0) {
                return Object.entries(localConfig)
                  .filter(([key]) => allowedFields.includes(key))
                  .map(([key, value]) => renderConfigField(key, value));
              }

              // Fallback: filter out complex values and internal fields
              return Object.entries(localConfig)
                .filter(([key, value]) => {
                  const pnlFields = [
                    "openingCommission", "closingCommission",
                    "pipSize", "pipValueInUsd", "swapFees",
                    "contractMultiplier", "quantityContracts",
                    "pnlMultiplier", "tickValue",
                    "contractType", "positionSize", "fundingRate", "fundingTimestamp", "currentMarkPrice"
                  ];
                  const isComplexValue = (typeof value === "object" && value !== null && !(value instanceof Date)) ||
                    Array.isArray(value);
                  return !key.startsWith("_") &&
                    !pnlFields.includes(key) &&
                    !isComplexValue;
                })
                .map(([key, value]) => renderConfigField(key, value));
            })()}

            {/* P&L Configuration Section for Symbol nodes */}
            {node.type === "Symbol" && (
              <div className="mt-4 pt-3 border-top">
                <h6 className="text-primary mb-3">
                  <i className="fas fa-calculator me-2"></i>
                  P&L Calculation Configuration
                </h6>
                <div className="alert alert-info">
                  <small>
                    <strong>Note:</strong> These fields are used for accurate P&L calculations.
                    Configure them based on your specific instrument requirements.
                  </small>
                </div>
                {(() => {
                  // Get dynamic P&L fields based on asset class for rendering
                  const assetClass = localConfig.assetClass || resolvedAssetClass;
                  const assetSpecificFields: Record<string, string[]> = {
                    "Stocks": [], // No asset-specific P&L fields
                    "Forex": ["pipSize", "pipValueInUsd", "swapFees"],
                    "Stock Options": ["contractMultiplier", "quantityContracts"],
                    "Futures": ["pnlMultiplier", "tickValue"],
                    "Futures Options": ["pnlMultiplier", "tickValue"], // Same as Futures
                    "Crypto Spot": [], // No asset-specific P&L fields
                    "Crypto Futures": ["contractType", "positionSize", "fundingRate", "fundingTimestamp", "currentMarkPrice"]
                  };

                  const universalFields = ["openingCommission", "closingCommission"];
                  const relevantFields = assetSpecificFields[assetClass] || [];
                  const pnlFields = [...universalFields, ...relevantFields];

                  const pnlEntries = Object.entries(localConfig)
                    .filter(([key]) => {
                      return pnlFields.includes(key);
                    });

                  if (pnlEntries.length === 0) {
                    if (!assetClass) {
                      return (
                        <div className="alert alert-info">
                          <strong>Info:</strong> Please select an Asset Class to see relevant P&L configuration fields.
                        </div>
                      );
                    } else {
                      return (
                        <div className="alert alert-warning">
                          <strong>Debug:</strong> No P&L fields found for asset class "{assetClass}".
                          Available keys: {Object.keys(localConfig).join(", ")}
                        </div>
                      );
                    }
                  }

                  return pnlEntries.map(([key, value]) => renderConfigField(key, value));
                })()}
              </div>
            )}
          </Form>
        )}
      </PanelBody>
    </PanelWrapper>
  );
};

export default ConfigurationPanel;
